import { formatBRL, formatDateBR, monthYearLongBR, formatDateLongBR } from './formatters';

// Detecta se o documento e CPF (11 digitos) ou CNPJ (14). Usado pra
// nao rotular "CNPJ" em cima de um CPF de tomador PF (hoje: BHAZ).
const docLabelOf = (v) => String(v || '').replace(/\D/g, '').length === 11 ? 'CPF' : 'CNPJ';

const ALIQUOTA_MAP = {
  ISS: 'aliquota_iss', PIS: 'aliquota_pis', COFINS: 'aliquota_cofins',
  CSLL: 'aliquota_csll', IR: 'aliquota_ir', INSS: 'aliquota_inss',
};
const fmtPct = (n) =>
  Number(n || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + '%';

function calcularRetencoes(solicitacao, servico) {
  const valorBruto = Number(solicitacao.valor) || 0;
  const retidos = (solicitacao.impostos_retidos || []).filter(r => r !== 'Sem retenção');
  const linhas = retidos.map(nome => {
    const col = ALIQUOTA_MAP[nome];
    const raw = col ? servico?.[col] : null;
    const cadastrada = raw !== null && raw !== undefined && raw !== '';
    const aliquota = cadastrada ? Number(raw) : 0;
    const valor = cadastrada ? (aliquota / 100) * valorBruto : 0;
    return { nome, aliquota, cadastrada, valor };
  });
  const totalRetido = linhas.filter(l => l.cadastrada).reduce((a, l) => a + l.valor, 0);
  return { linhas, valorLiquido: valorBruto - totalRetido, valorBruto };
}

/**
 * Versao texto puro do espelho — pra mailto: (que nao suporta HTML
 * confiavel cross-client). Mantem hierarquia visual com linhas separadoras.
 */
export function buildEspelhoEmailText({ solicitacao, empresa, tomador, servico }) {
  const ret = calcularRetencoes(solicitacao, servico);
  const linhasCadastradas = ret.linhas.filter(l => l.cadastrada);

  const lines = [];
  lines.push('Prezado(a),');
  lines.push('');
  lines.push('Segue o espelho da solicitação de nota fiscal para sua validação:');
  lines.push('');
  lines.push('─────────────────────────────────────────');
  lines.push(`ESPELHO · SOLICITAÇÃO DE NF`);
  lines.push(`ID: ${solicitacao.codigo_unico}`);
  if (solicitacao.pi) lines.push(`PI: ${solicitacao.pi}`);
  lines.push('─────────────────────────────────────────');
  lines.push('');

  // Prestador
  lines.push('PRESTADOR');
  lines.push(empresa?.razao_social || '—');
  if (empresa?.cnpj) lines.push(`CNPJ ${empresa.cnpj}`);
  const enderecoEmp = [
    empresa?.logradouro && `${empresa.logradouro}${empresa.numero ? ', ' + empresa.numero : ''}`,
    empresa?.bairro,
    empresa?.cidade && empresa?.uf && `${empresa.cidade}/${empresa.uf}`,
    empresa?.cep && `CEP ${empresa.cep}`,
  ].filter(Boolean).join(' · ');
  if (enderecoEmp) lines.push(enderecoEmp);
  const inscEmp = [
    empresa?.inscricao_estadual && `IE ${empresa.inscricao_estadual}`,
    empresa?.inscricao_municipal && `IM ${empresa.inscricao_municipal}`,
  ].filter(Boolean).join(' · ');
  if (inscEmp) lines.push(inscEmp);
  lines.push('');

  // Tomador
  lines.push('TOMADOR');
  lines.push(tomador?.razao_social || '—');
  if (tomador?.cnpj) lines.push(`${docLabelOf(tomador.cnpj)} ${tomador.cnpj}`);
  const enderecoTom = [
    tomador?.logradouro && `${tomador.logradouro}${tomador.numero ? ', ' + tomador.numero : ''}`,
    tomador?.bairro,
    tomador?.cidade && tomador?.uf && `${tomador.cidade}/${tomador.uf}`,
    tomador?.cep && `CEP ${tomador.cep}`,
  ].filter(Boolean).join(' · ');
  if (enderecoTom) lines.push(enderecoTom);
  lines.push('');

  // Intermediário
  if (solicitacao.possui_intermediario) {
    lines.push('INTERMEDIÁRIO');
    lines.push(solicitacao.intermediario_razao_social || '—');
    if (solicitacao.intermediario_cnpj) lines.push(`CNPJ ${solicitacao.intermediario_cnpj}`);
    lines.push('');
  }

  // Headline
  lines.push('─────────────────────────────────────────');
  lines.push(`VALOR BRUTO:  ${formatBRL(solicitacao.valor)}`);
  lines.push(`COMPETÊNCIA:  ${monthYearLongBR(solicitacao.data_prestacao_servico)}`);
  lines.push('─────────────────────────────────────────');
  lines.push('');

  // Detalhes
  lines.push(`Data da solicitação: ${formatDateBR(solicitacao.data_solicitacao)}`);
  lines.push(`Serviço: ${servico ? `${servico.codigo} — ${servico.descricao}` : '—'}`);
  lines.push(`Descrição: ${solicitacao.descricao || '—'}`);
  lines.push('');

  // Retenções
  if (linhasCadastradas.length > 0) {
    lines.push('Tributos aproximados:');
    linhasCadastradas.forEach(l => {
      const nome = l.nome === 'ISS' ? 'ISSQN' : l.nome;
      lines.push(`  • ${nome} · ${fmtPct(l.aliquota)} = ${formatBRL(l.valor)}`);
    });
    lines.push(`Valor líquido aproximado: ${formatBRL(ret.valorLiquido)}`);
    lines.push('');
  }

  if (solicitacao.informacoes_complementares) {
    lines.push(`Informações complementares: ${solicitacao.informacoes_complementares}`);
    lines.push('');
  }

  lines.push(`Solicitante (Comercial): ${solicitacao.solicitante}`);
  lines.push('');

  // Aviso
  lines.push('─────────────────────────────────────────');
  lines.push('Quaisquer solicitações de alteração — incluindo correções de valor, escopo, dados cadastrais ou retenções tributárias — deverão ser apresentadas até o 5º (quinto) dia do mês subsequente ao da competência da nota fiscal. Decorrido esse prazo, NÃO SERÃO ADMITIDOS cancelamentos nem emissões substitutivas, em razão do recolhimento prévio dos tributos incidentes (PIS, COFINS, ISS, IRRF, CSLL e demais retenções devidas) e do encerramento da apuração fiscal do período. Eventuais divergências identificadas após esse prazo deverão ser tratadas comercialmente entre as partes, sem reflexo direto na nota já escriturada.');
  lines.push('');
  lines.push(`${empresa?.cidade || '—'}, ${formatDateLongBR(solicitacao.data_solicitacao)}.`);

  return lines.join('\n');
}

export function buildEspelhoEmailSubjectShort({ solicitacao, empresa, tomador }) {
  const empNome = empresa?.nome_fantasia || empresa?.razao_social || '—';
  const tomNome = tomador?.nome_fantasia || tomador?.razao_social || '—';
  return `Solicitação de NF · ${empNome} → ${tomNome} · ${solicitacao.codigo_unico}`;
}

/**
 * Monta a mailto: URL e abre no client default do user.
 * Compose new tab no Gmail web (se for o default), Outlook, Apple Mail, etc.
 */
export function openMailtoEspelho({ solicitacao, empresa, tomador, servico, to, cc, bcc }) {
  const subject = buildEspelhoEmailSubjectShort({ solicitacao, empresa, tomador });
  const body = buildEspelhoEmailText({ solicitacao, empresa, tomador, servico });

  const params = new URLSearchParams();
  params.set('subject', subject);
  params.set('body', body);
  if (cc) params.set('cc', cc);
  if (bcc) params.set('bcc', bcc);

  const toStr = Array.isArray(to) ? to.join(',') : (to || '');
  const url = `mailto:${encodeURIComponent(toStr)}?${params.toString()}`;
  window.location.href = url;
}

/* =============================================================================
   Versao da NF EMITIDA — texto puro pra mailto
   ============================================================================= */

export function buildNFEmitidaEmailText({ solicitacao, empresa, tomador, servico }) {
  const ret = calcularRetencoes(solicitacao, servico);
  const linhasCadastradas = ret.linhas.filter(l => l.cadastrada);

  const lines = [];
  lines.push('Prezado(a),');
  lines.push('');
  lines.push('Conforme espelho previamente aprovado entre as partes, a NOTA FISCAL DE SERVIÇOS foi emitida.');
  lines.push('');

  if (solicitacao.anexo_nota_url) {
    lines.push('🔗 Arquivo da NF (link válido por 1 ano):');
    lines.push(solicitacao.anexo_nota_url);
    lines.push('');
  } else {
    lines.push('⚠ Arquivo da NF NÃO foi anexado a este envio.');
    lines.push('');
  }

  lines.push('─────────────────────────────────────────');
  lines.push(`NOTA FISCAL · EMITIDA`);
  if (solicitacao.numero_nota) lines.push(`Nº da nota: ${solicitacao.numero_nota}`);
  lines.push(`Solicitação: ${solicitacao.codigo_unico}`);
  lines.push('─────────────────────────────────────────');
  lines.push('');

  lines.push(`PRESTADOR: ${empresa?.razao_social || '—'}`);
  if (empresa?.cnpj) lines.push(`CNPJ ${empresa.cnpj}`);
  lines.push('');
  lines.push(`TOMADOR: ${tomador?.razao_social || '—'}`);
  if (tomador?.cnpj) lines.push(`${docLabelOf(tomador.cnpj)} ${tomador.cnpj}`);
  lines.push('');

  lines.push(`Valor bruto: ${formatBRL(solicitacao.valor)}`);
  lines.push(`Competência: ${monthYearLongBR(solicitacao.data_prestacao_servico)}`);
  lines.push(`Data de emissão: ${formatDateBR(solicitacao.data_solicitacao)}`);
  lines.push(`Serviço: ${servico ? `${servico.codigo} — ${servico.descricao}` : '—'}`);
  lines.push(`Descrição: ${solicitacao.descricao || '—'}`);
  lines.push('');

  if (linhasCadastradas.length > 0) {
    lines.push('Tributos retidos:');
    linhasCadastradas.forEach(l => {
      const nome = l.nome === 'ISS' ? 'ISSQN' : l.nome;
      lines.push(`  • ${nome} · ${fmtPct(l.aliquota)} = ${formatBRL(l.valor)}`);
    });
    lines.push(`Valor líquido aproximado: ${formatBRL(ret.valorLiquido)}`);
    lines.push('');
  }

  lines.push('─────────────────────────────────────────');
  lines.push('Em caso de divergência entre os dados informados nesta nota e os contratualmente acordados, favor entrar em contato em até o 5º (quinto) dia do mês subsequente ao da competência. Após esse prazo, em razão do recolhimento prévio dos tributos incidentes, eventuais ajustes deverão ser tratados comercialmente entre as partes.');
  lines.push('');
  lines.push(`${empresa?.cidade || '—'}, ${formatDateLongBR(solicitacao.data_solicitacao)}.`);

  return lines.join('\n');
}

export function buildNFEmitidaEmailSubjectShort({ solicitacao, empresa, tomador }) {
  const empNome = empresa?.nome_fantasia || empresa?.razao_social || '—';
  const tomNome = tomador?.nome_fantasia || tomador?.razao_social || '—';
  const numNota = solicitacao.numero_nota ? `NF ${solicitacao.numero_nota}` : solicitacao.codigo_unico;
  return `Nota fiscal emitida · ${empNome} → ${tomNome} · ${numNota}`;
}

export function openMailtoNFEmitida({ solicitacao, empresa, tomador, servico, to, cc, bcc }) {
  const subject = buildNFEmitidaEmailSubjectShort({ solicitacao, empresa, tomador });
  const body = buildNFEmitidaEmailText({ solicitacao, empresa, tomador, servico });

  const params = new URLSearchParams();
  params.set('subject', subject);
  params.set('body', body);
  if (cc) params.set('cc', cc);
  if (bcc) params.set('bcc', bcc);

  const toStr = Array.isArray(to) ? to.join(',') : (to || '');
  const url = `mailto:${encodeURIComponent(toStr)}?${params.toString()}`;
  window.location.href = url;
}
